/**
 * '고객 분석' 의 **가입 요일 x 가입 시간대 히트맵** 집계(2026-09-11 사용자 요청).
 *
 * ## 왜 서버가 교차표를 따로 주는가
 *
 * 화면에 이미 있던 네 분포(성별·연령대·시간대·요일)는 **주변분포**다 —
 * "20대가 몇 %", "월요일이 몇 %" 는 알지만 **"20대가 월요일 몇 시에 오는가" 는
 * 원리적으로 조합해 낼 수 없다.** 그래서 서버가 `joinHeatmap` 에
 * `(age, gender, day, hour, count)` 칸들을 세어 내려준다.
 *
 * ## 필터는 화면에서 한다
 *
 * 서버가 준 칸 목록을 **연령대·성별로 걸러 다시 합친다.** 필터를 바꿀 때마다 서버를 부르지
 * 않으므로 즉시 반응하고, 같은 데이터로 여러 조합을 비교할 수 있다.
 * 칸 수 상한은 나이 10 x 성별 3 x 요일 7 x 시간대 13 = 2,730 으로 **고객 수와 무관**하다.
 */
import { DAY_BUCKETS, HOUR_BUCKETS } from './customerDistribution'

/** 세로축 = 요일(월->일), 가로축 = 시간대(0~6시 -> 21~24시). 막대그래프와 **같은 순서**다. */
export const HEATMAP_DAYS = DAY_BUCKETS.map(([key, label]) => ({ key, label }))
export const HEATMAP_HOURS = HOUR_BUCKETS.map(([key, label]) => ({ key, label }))

export const ALL = '전체'

/**
 * 필터 선택 목록. **데이터에 실제로 있는 값만** 고르게 한다 —
 * 고정 목록을 쓰면 아무 칸도 없는 연령대를 골라 빈 히트맵을 보게 된다.
 *
 * 연령대는 나이 순으로, '미수집' 은 맨 뒤로 보낸다.
 */
export const heatmapFilterOptions = (cells) => {
    const list = cells ?? []
    const ageOrder = ['10대이하', '20~24', '25~29', '30~34', '35~39', '40~44', '45~49', '50~54', '55~59', '60대이상']
    const genderOrder = ['여성', '남성', '미수집']

    const present = (values, order) => {
        const found = new Set(values)
        const known = order.filter((v) => found.has(v))
        const unknown = [...found].filter((v) => !order.includes(v)).sort()
        return [ALL, ...known, ...unknown]
    }

    return {
        ages: present(list.map((c) => c.age), ageOrder),
        genders: present(list.map((c) => c.gender), genderOrder),
    }
}

/**
 * 필터를 적용해 ECharts 히트맵 데이터로 바꾼다.
 *
 * @returns `{ cells, max, total }`
 *   `cells` = `[hourIndex, dayIndex, count]`. 🔑 **0건 칸도 전부 넣는다** —
 *   빼면 그 자리가 빈 구멍으로 남아 "데이터가 없는 것" 과 "0명인 것" 이 같아 보인다.
 *   `max` = 색 스케일 상단(0건만 있으면 0).
 *
 * ⚠️ 서버 버킷 문구가 바뀌어 격자에 없는 요일·시간대가 오면 그 칸은 **그려지지 않지만**
 *    `total` 에는 남는다 — 축이 늘어나 배치가 깨지는 것보다 낫고, 칸 합과 전체가 어긋나면
 *    문구가 갈렸다는 신호가 된다.
 */
export const toHeatmapMatrix = (cells, { age = ALL, gender = ALL } = {}) => {
    const matched = (cells ?? []).filter((c) =>
        (age === ALL || c.age === age) && (gender === ALL || c.gender === gender))

    // (요일, 시간대) -> 인원. 키를 문자열로 합쳐 한 번만 훑는다.
    const sums = new Map()
    let total = 0
    matched.forEach((c) => {
        const key = `${c.day} ${c.hour}`
        sums.set(key, (sums.get(key) ?? 0) + (c.count ?? 0))
        total += c.count ?? 0
    })

    const out = []
    let max = 0
    HEATMAP_DAYS.forEach((day, dayIndex) => {
        HEATMAP_HOURS.forEach((hour, hourIndex) => {
            const count = sums.get(`${day.key} ${hour.key}`) ?? 0
            if (count > max) max = count
            out.push([hourIndex, dayIndex, count])
        })
    })

    return { cells: out, max, total }
}

/**
 * 가장 많이 몰린 칸 한 줄 요약. 히트맵만 보여주면 "그래서 어디가 피크인가" 를 눈으로 찾아야 한다.
 * @returns `null` = 아무 데이터도 없다.
 */
export const heatmapPeak = (matrix) => {
    if (!matrix || matrix.max <= 0) return null
    const top = matrix.cells.find((c) => c[2] === matrix.max)
    if (!top) return null
    return {
        day: HEATMAP_DAYS[top[1]].label,
        hour: HEATMAP_HOURS[top[0]].label,
        count: top[2],
        share: matrix.total > 0 ? Math.round((top[2] / matrix.total) * 1000) / 10 : 0,
    }
}
